import { useMemo, useState } from 'react'
import { HelpCircle } from 'lucide-react'
import {
  answerDecisionGap,
  type DecisionGapItem,
  type DecisionGapQuestion,
} from '../../services/prioritizationService'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'
import { AskEyebrow } from '../today/AskEyebrow'

/**
 * AutoMom's tone-guarded "fill the gaps" moment. When the LifeMap engine is
 * missing a must-have signal (or only has a weak LLM guess), yanamas asks ONE
 * caring question at a time — never "fill missing metadata". The answer is
 * persisted with source=user, so it overrides any prior inference.
 *
 * Renders nothing when the engine is off or there are no gaps, so it is
 * always safe to mount.
 */
const LEVEL_LABEL: Record<string, string> = {
  low: 'Light',
  medium: 'Medium',
  high: 'Heavy',
  soft: 'Some flex',
  hard: 'Hard deadline',
}

/**
 * Reports whether this card has anything to show, so the ranked ask zone can
 * skip it (2026-09-08).
 *
 * These cards decide for themselves whether to render, and return null when
 * they have nothing. That was invisible while they sat in a plain stack. Once
 * they became rows in a BUDGETED list it stopped being invisible: an empty
 * card still took a slot and still counted toward "N to answer", so the zone
 * could promise five asks and show one.
 */
/**
 * Presentational since 2026-09-08 — see the note on AdmissionInbox. The page
 * fetches the gaps so the ask zone's count is right before it is opened.
 */
export function FillTheGapsCard({ gaps }: { gaps: DecisionGapItem[] }) {
  const [answered, setAnswered] = useState<Set<string>>(new Set())
  const [textValue, setTextValue] = useState('')

  // The single next question across all items (one at a time, never a form).
  const current: { item: DecisionGapItem; q: DecisionGapQuestion } | null =
    useMemo(() => {
      for (const item of gaps) {
        for (const q of item.questions) {
          if (!answered.has(`${q.item_id}:${q.field}`)) return { item, q }
        }
      }
      return null
    }, [gaps, answered])

  // Two questions per visit, then stop — one caring ask must never turn
  // into an interview ("yanamas is asking me a lot", owner 2026-08-24).
  // The rest of the gaps keep for the next visit.
  if (answered.size >= 2) return null

  if (!current) return null

  const { q } = current

  const submit = async (value: string | number | boolean) => {
    const key = `${q.item_id}:${q.field}`
    const ok = await answerDecisionGap({
      item_id: q.item_id,
      item_type: q.item_type,
      field: q.field,
      value,
    })
    if (ok) {
      setAnswered((prev) => new Set(prev).add(key))
      setTextValue('')
    }
  }

  return (
    <div className="rounded-2xl border border-brand-subtle-border bg-gradient-to-br from-brand-subtle to-surface p-4 shadow-sm">
      <AskEyebrow icon={<HelpCircle />} label={ORI_KIND_LABEL.quick_answer} />

      {/* Typography contract (owner 2026-08-27): the item the question is
          ABOUT is the thing the user must recognize — it was the smallest
          text on the card. Name first, prominently; question after. */}
      <p className="mt-2 text-sm text-muted">
        About{' '}
        <span className="text-base font-semibold text-primary" style={{ fontFamily: 'var(--font-display)' }}>
          “{q.item_title}”
        </span>
      </p>
      <p className="mt-1.5 text-base leading-snug text-secondary">
        {q.question}
      </p>

      {q.kind === 'text' ? (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (textValue.trim()) submit(textValue.trim())
          }}
        >
          <input
            type="text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder="In your own words…"
            className="min-h-touch flex-1 rounded-xl border border-brand-subtle-border bg-surface px-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
          <button
            type="submit"
            disabled={!textValue.trim()}
            className="min-h-touch rounded-xl bg-brand px-4 text-sm font-bold text-on-brand shadow-[0_4px_12px_rgba(120,81,169,0.3)] hover:bg-brand-hover disabled:opacity-50 transition-colors"
          >
            Save
          </button>
        </form>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {q.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => submit(opt)}
              className="min-h-touch rounded-xl border border-brand-subtle-border bg-surface/70 px-4 text-sm text-secondary hover:bg-surface-alt transition-colors"
            >
              {LEVEL_LABEL[opt] ?? opt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
